import { useEffect, useMemo, useState } from "react";
import { DollarSign, Eye, Radio } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { usePageTitle } from "@/components/common/AppShell";
import StatCard from "@/components/common/StatCard";
import DataTable, { DATA_TABLE_CLASSES } from "@/components/common/DataTable";
import DateRangePicker from "@/components/common/DateRangePicker";

const DEFAULT_RANGE_DAYS = 30;

const defaultDateRange = () => {
  const to = new Date();
  to.setHours(23, 59, 59, 999);
  const from = new Date();
  from.setDate(from.getDate() - (DEFAULT_RANGE_DAYS - 1));
  from.setHours(0, 0, 0, 0);
  return { from, to };
};

// /report/mine takes calendar dates (inclusive both ends), in the publisher's
// own local day.
const toCalendarDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatMoney = (value) =>
  `$${(Number(value) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDay = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

// The publisher's own payout: what we owe them per day over the endpoints an
// admin linked to this login. The API carries no sell price, margin or
// campaign, so nothing here can be sliced that way.
export default function EarningsPage() {
  usePageTitle("Earnings");
  const { performRequest } = useApp();
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const from = dateRange?.from ? toCalendarDate(dateRange.from) : null;
  const to = dateRange?.to ? toCalendarDate(dateRange.to) : null;

  useEffect(() => {
    if (!from || !to) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    performRequest(
      `/report/mine?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    )
      .then((data) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Failed to load earnings", err);
        if (!cancelled) {
          setRows([]);
          setError(err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [performRequest, from, to]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          owed: acc.owed + (Number(r.owed) || 0),
          impressions: acc.impressions + (Number(r.impressions) || 0),
          bids: acc.bids + (Number(r.bids) || 0),
        }),
        { owed: 0, impressions: 0, bids: 0 },
      ),
    [rows],
  );

  const compact = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 1,
      }),
    [],
  );

  return (
    <section className="flex min-w-0 flex-col">
      <div className="mb-[26px] flex flex-wrap items-start justify-between gap-4">
        <p className="text-[13.5px] text-(--text-muted)">
          What you have earned from the traffic you sent us, per day.
        </p>
        <DateRangePicker value={dateRange} onChange={setDateRange} />
      </div>

      <div className="mb-[26px] grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          label="Earned"
          value={loading ? "…" : formatMoney(totals.owed)}
          trend="Selected range"
          icon={<DollarSign size={22} strokeWidth={1.8} />}
          accent="green"
        />
        <StatCard
          label="Impressions"
          value={loading ? "…" : compact.format(totals.impressions)}
          trend="Confirmed by the player"
          icon={<Eye size={22} strokeWidth={1.8} />}
          accent="violet"
        />
        <StatCard
          label="Ads served"
          value={loading ? "…" : compact.format(totals.bids)}
          trend="Responses we returned"
          icon={<Radio size={22} strokeWidth={1.8} />}
          accent="violet"
        />
      </div>

      <div className="rounded-(--radius-lg) border border-(--border-strong) bg-(--surface-1) p-[22px]">
        <div className="mb-4">
          <h3 className="text-[15px] font-bold text-(--text-primary)">
            Daily breakdown
          </h3>
          <p className="mt-1 text-[12.5px] text-(--text-muted)">
            Days with no activity are omitted.
          </p>
        </div>
        {loading ? (
          <div className="px-4 py-8 text-center text-(--text-muted)">
            Loading earnings…
          </div>
        ) : error ? (
          <div className="px-4 py-8 text-center text-(--status-danger)">
            Could not load earnings. Please try again later.
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-8 text-center text-(--text-muted)">
            No activity in this range.
          </div>
        ) : (
          <DataTable>
            <thead>
              <tr className={DATA_TABLE_CLASSES.headRow}>
                <th className={DATA_TABLE_CLASSES.th}>Day</th>
                <th className={DATA_TABLE_CLASSES.thRight}>Ads served</th>
                <th className={DATA_TABLE_CLASSES.thRight}>Impressions</th>
                <th className={DATA_TABLE_CLASSES.thRight}>Earned</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.date} className={DATA_TABLE_CLASSES.bodyRow}>
                  <td className={`${DATA_TABLE_CLASSES.td} font-semibold`}>
                    {formatDay(r.date)}
                  </td>
                  <td className={DATA_TABLE_CLASSES.tdRight}>
                    {Number(r.bids || 0).toLocaleString("en-US")}
                  </td>
                  <td className={DATA_TABLE_CLASSES.tdRight}>
                    {Number(r.impressions || 0).toLocaleString("en-US")}
                  </td>
                  <td
                    className={`${DATA_TABLE_CLASSES.tdRight} font-bold text-(--status-success)`}
                  >
                    {formatMoney(r.owed)}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </div>
    </section>
  );
}
